/**
 * The last home payload, per uid, in localStorage (PPL-416).
 *
 * The MessageCacheContext idea: a fresh entry (inside the TTL) is rendered
 * straight away and refreshed in the background; an older one is only the
 * offline / error fallback, dimmed and stamped "as of". Never a source of
 * truth — the server's feed replaces it on every successful fetch — so the
 * shape check is deliberately loose: anything that is not a feed is a miss.
 */

import type { HomeFeed } from '../../types/home';

export const HOME_FEED_CACHE_PREFIX = 'pplkpr_home_feed_v1:';
export const HOME_FEED_TTL_MS = 5 * 60 * 1000;

export interface CachedHomeFeed {
  feed: HomeFeed;
  storedAt: number;
}

function key(uid: string): string {
  return `${HOME_FEED_CACHE_PREFIX}${uid}`;
}

const STAGES = new Set(['S0', 'S1', 'S2', 'S3', 'S4']);

/** True when `value` has the bones of a HomeFeed. Payload contents are the server's business. */
export function looksLikeFeed(value: unknown): value is HomeFeed {
  if (!value || typeof value !== 'object') return false;
  const f = value as Record<string, unknown>;
  return (
    typeof f.stage === 'string' &&
    STAGES.has(f.stage) &&
    Array.isArray(f.modules) &&
    Array.isArray(f.prompts) &&
    typeof f.asOf === 'string'
  );
}

/**
 * Coerce whatever the callable (or the cache) handed us into a feed with
 * every list present, dropping modules that could not be rendered or
 * dismissed (no type / no dismissKey). Unknown module TYPES are kept — the
 * registry renders them as nothing (forward compatibility).
 */
export function normalizeFeed(raw: unknown): HomeFeed | null {
  if (!looksLikeFeed(raw)) return null;
  const f = raw as HomeFeed & Record<string, unknown>;
  const clean = (list: unknown) =>
    (Array.isArray(list) ? list : []).filter(
      (m) => m && typeof m === 'object' && typeof m.type === 'string' && typeof m.dismissKey === 'string' && m.payload && typeof m.payload === 'object',
    );
  const modules = clean(f.modules);
  const overflow = clean(f.overflow);
  return {
    stage: f.stage,
    pillLabel: typeof f.pillLabel === 'string' && f.pillLabel ? f.pillLabel : 'f8 · ready',
    opener: f.opener && typeof f.opener === 'object' && typeof f.opener.text === 'string' && f.opener.text ? f.opener : null,
    prompts: f.prompts.filter((p): p is string => typeof p === 'string' && p.length > 0),
    modules,
    more: typeof f.more === 'number' && f.more > 0 ? f.more : overflow.length,
    overflow,
    quietDay: f.quietDay === true || (modules.length === 0 && overflow.length === 0),
    asOf: f.asOf,
  };
}

export function readHomeFeedCache(uid: string): CachedHomeFeed | null {
  try {
    const stored = localStorage.getItem(key(uid));
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { feed?: unknown; storedAt?: unknown };
    const feed = normalizeFeed(parsed?.feed);
    if (!feed || typeof parsed.storedAt !== 'number') return null;
    return { feed, storedAt: parsed.storedAt };
  } catch {
    return null;
  }
}

export function writeHomeFeedCache(uid: string, feed: HomeFeed): void {
  try {
    localStorage.setItem(key(uid), JSON.stringify({ feed, storedAt: Date.now() } satisfies CachedHomeFeed));
  } catch {
    /* quota / private mode — the cache is a convenience */
  }
}

export function clearHomeFeedCache(uid: string): void {
  try {
    localStorage.removeItem(key(uid));
  } catch {
    /* no-op */
  }
}

export function isFresh(entry: CachedHomeFeed, now: number = Date.now()): boolean {
  return now - entry.storedAt < HOME_FEED_TTL_MS;
}

/* ------------------------------------------------------------------ *
 * The stale flag (PPL-488)
 * ------------------------------------------------------------------ */

/**
 * uids whose desk is known to be out of date. The stage a feed was built
 * from can change outside the desk — saving a profile is the case that
 * matters: an interest alone now leaves S0 — and neither the 5-minute cache
 * TTL nor the focus throttle would notice, so the user walks back from
 * edit-profile onto the S0 desk they just left.
 *
 * In memory, not localStorage: it only has to outlive a route change inside
 * the SPA, and a reload fetches anyway.
 */
const staleUids = new Set<string>();
/** Set by a uid-less call — the caller knows the desk changed, not for whom. */
let staleAll = false;

/**
 * Mark the desk stale so the next mount or focus revalidates instead of
 * adopting what it has. Callers that also hold a stale localStorage copy
 * should `clearHomeFeedCache(uid)` alongside it.
 */
export function markHomeFeedStale(uid?: string): void {
  if (uid) staleUids.add(uid);
  else staleAll = true;
}

export function isHomeFeedStale(uid: string): boolean {
  return staleAll || staleUids.has(uid);
}

/** Cleared once the revalidating fetch is on its way. */
export function clearHomeFeedStale(uid: string): void {
  staleAll = false;
  staleUids.delete(uid);
}
